import { useToast } from "components/atoms/toast/toast-context";
import { ModalSize } from "components/modal/modal.types";
import { useRouter } from "next/router";
import { useEffect, useReducer } from "react";
import { IApiError } from "services/api/api.types";
import {
  skipNpsFeedback,
  submitNpsRating,
  updateNpsFeedback
} from "services/api/nps/npsService";
import {
  ISkipFeedbackPayload,
  ISubmitRatingPayload
} from "services/api/nps/npsService.types";
import { createParentDetails } from "services/api/users/user-services";
import { ICreateParentPayload } from "services/api/users/user-services.types";
import trackEvent from "services/mixpanel/mixpanel";
import { npsEventNames } from "services/mixpanel/nps-events";
import { useAuth } from "stores/auth/auth-context";
import { CountryCode } from "types/common/common.types";
import {
  createPayloadForNPSTrackingEvent,
  getNPSSource
} from "utils/mixpanel/nps/nps-mixpanel-utils";
import { npsInitialState, npsReducer } from "../reducer";
import { NpsInitialStateType } from "../nps-reducer.types";
import {
  IUseNpsModal,
  IUseNpsModalProps,
  NPSModalPhases,
  ToggleSkipConfirmationTypes
} from "./nps-modal.types";
import { parseAPIErrorResponse } from "utils/utils";
import * as Sentry from "@sentry/nextjs";

export const useNpsModal = ({
  onClose,
  feedbackTrialClassDataObject
}: IUseNpsModalProps): IUseNpsModal => {
  const router = useRouter();
  const [state, dispatch] = useReducer(npsReducer, {
    ...npsInitialState,
    liveClassId: feedbackTrialClassDataObject.id
  });
  const {
    user,
    user: { userData }
  } = useAuth();
  const { access_token, student_id } = userData.primary_user;
  const { addToast } = useToast();

  useEffect(() => {
    const eventPayload = createPayloadForNPSTrackingEvent({
      useAuthData: user,
      classStartTime: feedbackTrialClassDataObject.start_date,
      classSubject: feedbackTrialClassDataObject.subject,
      classTopic: feedbackTrialClassDataObject.topic,
      trialClassId: state.liveClassId,
      source: getNPSSource(router)
    });
    trackEvent({
      eventName: npsEventNames.npsFeedbackScreenViewed,
      payload: eventPayload
    });
  }, []);

  // modal close
  const handleModalClose = () => {
    dispatch({
      type: "CLOSE_NPS_MODAL",
      payload: npsInitialState
    });
    router.push({
      pathname: router.pathname
    });
    onClose();
  };

  /* Flow B (>7) */
  /* Submit Parent Number Form */
  const submitParentNumberForm = async (phoneNumber: string) => {
    const createParentPayload: ICreateParentPayload = {
      parent: {
        name: "",
        phone_number: phoneNumber,
        country_code: CountryCode.Indonesia,
        notification_consent: true,
        source: "nps",
        salutation: ""
      }
    };

    const eventPayload = createPayloadForNPSTrackingEvent({
      useAuthData: user,
      classStartTime: feedbackTrialClassDataObject.start_date,
      classSubject: feedbackTrialClassDataObject.subject,
      classTopic: feedbackTrialClassDataObject.topic,
      trialClassId: state.liveClassId,
      source: getNPSSource(router),
      npsRating: state.rating,
      parentNumber: phoneNumber
    });
    trackEvent({
      eventName: npsEventNames.npsSubmitClickedOnParentNumberForm,
      payload: eventPayload
    });
    try {
      const result = await createParentDetails(
        createParentPayload,
        access_token,
        student_id
      );

      const { message } = result;
      if (message === "OK") {
        dispatch({
          type: "SUBMIT_PARENT_NUMBER_FORM",
          payload: null
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      const errorResponse = parseAPIErrorResponse(error);
      const { message } = errorResponse as IApiError;
      addToast({
        heading: message,
        variant: "error"
      });
    }
  };
  const checkParentNumberIsAlreadySubmitted = () => {
    if (userData?.parent_user?.phone_number) {
      dispatch({
        type: "SUBMIT_PARENT_NUMBER_FORM",
        payload: null
      });
    }
  };

  /* Submit Rating Phase */
  const submitNpsFeedback = async (scale: string) => {
    const payload: ISubmitRatingPayload = {
      rating: scale,
      live_class_id: state.liveClassId
    };
    const eventPayload = createPayloadForNPSTrackingEvent({
      useAuthData: user,
      classStartTime: feedbackTrialClassDataObject.start_date,
      classSubject: feedbackTrialClassDataObject.subject,
      classTopic: feedbackTrialClassDataObject.topic,
      trialClassId: state.liveClassId,
      source: getNPSSource(router),
      npsRating: scale
    });
    trackEvent({
      eventName: npsEventNames.npsSubmitClickedOnFeedbackScreen,
      payload: eventPayload
    });
    try {
      const { data } = await submitNpsRating(payload, access_token);
      dispatch({
        type: "SUBMIT_NPS_FEEDBACK",
        payload: { scale, feedbackId: data!.id }
      });
    } catch (err) {
      Sentry.captureException(err);
      const error = parseAPIErrorResponse(err);

      const { message } = error as IApiError;
      addToast({ heading: message, variant: "error" });
      onClose();
    }
  };
  const closeNpsFeedback = (scale?: string) => {
    const eventPayload = createPayloadForNPSTrackingEvent({
      useAuthData: user,
      classStartTime: feedbackTrialClassDataObject.start_date,
      classSubject: feedbackTrialClassDataObject.subject,
      classTopic: feedbackTrialClassDataObject.topic,
      trialClassId: state.liveClassId,
      source: getNPSSource(router),
      npsRating: scale || ""
    });
    const eventName = scale
      ? npsEventNames.npsFeedbackScreenSkippedWithScore
      : npsEventNames.npsFeedbackScreenSkippedWithoutScore;
    trackEvent({
      eventName,
      payload: eventPayload
    });
    handleNpsFeedbackSkip();
  };
  const handleNpsFeedbackSkip = async () => {
    try {
      const payload: ISkipFeedbackPayload = {
        live_class_id: state.liveClassId,
        skipped: true
      };
      await skipNpsFeedback(payload, access_token);
    } catch (err) {
      Sentry.captureException(err);
      const error = parseAPIErrorResponse(err);

      const { message } = error as IApiError;
      addToast({ heading: message, variant: "error" });
    } finally {
      handleModalClose();
    }
  };
  const trackScaleChange = (scaleValue: string) => {
    const eventPayload = createPayloadForNPSTrackingEvent({
      useAuthData: user,
      classStartTime: feedbackTrialClassDataObject.start_date,
      classSubject: feedbackTrialClassDataObject.subject,
      classTopic: feedbackTrialClassDataObject.topic,
      trialClassId: state.liveClassId,
      source: getNPSSource(router),
      npsRating: scaleValue
    });
    const eventName = npsEventNames.npsRatingSelectedOnFeedbackScreen;
    trackEvent({
      eventName,
      payload: eventPayload
    });
  };
  /* Flow A (<7) */
  /* Submit Feedback Form  */
  const submitFeedbackExplanationForm = async (comment: string) => {
    const eventPayload = createPayloadForNPSTrackingEvent({
      useAuthData: user,
      classStartTime: feedbackTrialClassDataObject.start_date,
      classSubject: feedbackTrialClassDataObject.subject,
      classTopic: feedbackTrialClassDataObject.topic,
      trialClassId: state.liveClassId,
      source: getNPSSource(router),
      npsRating: state.rating,
      feedback: comment
    });
    trackEvent({
      eventName: npsEventNames.npsSubmitClickedOnFeedbackTextboxBS,
      payload: eventPayload
    });
    try {
      await updateNpsFeedback({ comment }, state.feedbackId, access_token);

      dispatch({
        type: "SUBMIT_FEEDBACK_EXPLANATION_FORM",
        payload: null
      });
    } catch (err) {
      Sentry.captureException(err);
      const error = parseAPIErrorResponse(err);

      const { message } = error as IApiError;
      addToast({ heading: message, variant: "error" });
    }
  };
  const closeFeedbackExplanationForm = (comment?: string) => {
    const eventPayload = createPayloadForNPSTrackingEvent({
      useAuthData: user,
      classStartTime: feedbackTrialClassDataObject.start_date,
      classSubject: feedbackTrialClassDataObject.subject,
      classTopic: feedbackTrialClassDataObject.topic,
      trialClassId: state.liveClassId,
      source: getNPSSource(router),
      npsRating: state.rating,
      feedback: comment || ""
    });

    trackEvent({
      eventName: npsEventNames.npsSkipClickedOnFeedbackTextboxBS,
      payload: eventPayload
    });
    handleModalClose();
  };
  /* skip confirmation */
  const toggleSkipConfirmation = async (
    from:
      | ToggleSkipConfirmationTypes.PARENT_PHONE_NUMBER_SCREEN
      | ToggleSkipConfirmationTypes.SKIP_CONFIRMATION_SCREEN,
    phoneNumber?: string
  ) => {
    if (from === ToggleSkipConfirmationTypes.PARENT_PHONE_NUMBER_SCREEN) {
      const eventPayload = createPayloadForNPSTrackingEvent({
        useAuthData: user,
        classStartTime: feedbackTrialClassDataObject.start_date,
        classSubject: feedbackTrialClassDataObject.subject,
        classTopic: feedbackTrialClassDataObject.topic,
        trialClassId: state.liveClassId,
        source: getNPSSource(router),
        npsRating: state.rating,
        parentNumber: phoneNumber || ""
      });
      const eventName = phoneNumber
        ? npsEventNames.npsSkipClickedWithNumberOnParentNumberForm
        : npsEventNames.npsSkipClickedWithoutNumberOnParentNumberForm;
      trackEvent({
        eventName,
        payload: eventPayload
      });
      dispatch({
        type: "TOGGLE_SKIP_CONFIRMATION",
        payload: true
      });
    } else {
      const eventPayload = createPayloadForNPSTrackingEvent({
        useAuthData: user,
        classStartTime: feedbackTrialClassDataObject.start_date,
        classSubject: feedbackTrialClassDataObject.subject,
        classTopic: feedbackTrialClassDataObject.topic,
        trialClassId: state.liveClassId,
        source: getNPSSource(router),
        npsRating: state.rating
      });

      trackEvent({
        eventName: npsEventNames.npsSubmitClickedOnReNudgeBS,
        payload: eventPayload
      });

      dispatch({
        type: "TOGGLE_SKIP_CONFIRMATION",
        payload: false
      });
    }
  };
  const closeSkipConfirmation = () => {
    const eventPayload = createPayloadForNPSTrackingEvent({
      useAuthData: user,
      classStartTime: feedbackTrialClassDataObject.start_date,
      classSubject: feedbackTrialClassDataObject.subject,
      classTopic: feedbackTrialClassDataObject.topic,
      trialClassId: state.liveClassId,
      source: getNPSSource(router),
      npsRating: state.rating
    });

    trackEvent({
      eventName: npsEventNames.npsSkipClickedOnReNudgeBS,
      payload: eventPayload
    });
    handleModalClose();
  };
  const closeModalAfterSuccess = () => {
    const eventPayload = createPayloadForNPSTrackingEvent({
      useAuthData: user,
      classStartTime: feedbackTrialClassDataObject.start_date,
      classSubject: feedbackTrialClassDataObject.subject,
      classTopic: feedbackTrialClassDataObject.topic,
      trialClassId: state.liveClassId,
      source: getNPSSource(router),
      npsRating: state.rating
    });

    trackEvent({
      eventName: npsEventNames.npsOkayClickedOnThanksScreen,
      payload: eventPayload
    });
    handleModalClose();
  };
  const getCurrentModalPhase = (state: NpsInitialStateType) => {
    if (!state.rating) return NPSModalPhases.RATING_PHASE;
    if (
      state.rating &&
      Number(state.rating) < 7 &&
      !state.isFeedbackFormSubmitted
    )
      return NPSModalPhases.FEEDBACK_PHASE;
    if (state.rating && state.isFeedbackFormSubmitted)
      return NPSModalPhases.THANKYOU_AFTER_FEEDBACK_FORM;
    if (
      state.rating &&
      !state.showSkipConfirmation &&
      Number(state.rating) >= 7 &&
      !state.isParentFormSubmitted
    )
      return NPSModalPhases.PARENT_NUMBER_INPUT_PHASE;
    if (state.rating && state.showSkipConfirmation)
      return NPSModalPhases.SKIP_CONFIRMATION_PHASE;
    if (state.rating && state.isParentFormSubmitted)
      return NPSModalPhases.THANKYOU_AFTER_PARENT_FORM;
  };

  const getCurrentModalSize = (state: NpsInitialStateType) => {
    const currentPhase = getCurrentModalPhase(state);
    if (currentPhase === NPSModalPhases.RATING_PHASE) return ModalSize.Wide;
    return ModalSize.Narrow;
  };

  return {
    handleModalClose,
    handleNpsFeedbackSkip,
    state,
    submitParentNumberForm,
    submitNpsFeedback,
    submitFeedbackExplanationForm,
    toggleSkipConfirmation,
    closeNpsFeedback,
    trackScaleChange,
    closeFeedbackExplanationForm,
    closeSkipConfirmation,
    closeModalAfterSuccess,
    getCurrentModalPhase,
    getCurrentModalSize,
    checkParentNumberIsAlreadySubmitted
  };
};
